import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { XCircle, BookOpen, Target, Wind, Shield, Zap, CheckCircle2, XOctagon, Clock, Crosshair, Activity } from 'lucide-react';
import { CTAButton } from '../components/CTAButton';
import { FAQItem } from '../components/FAQItem';
import { CountdownTimer } from '../components/CountdownTimer';
import { FloatingCTA } from '../components/FloatingCTA';
import { PaymentMethods } from '../components/PaymentMethods';
import { Footer } from '../components/Footer';
import { trackPageView } from '../utils/pixel';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay
}} data-id="element-78">
    {children}
  </motion.div>;
export function LandingPage() {
  useEffect(() => {
    trackPageView();
  }, []);
  return <>
      <CountdownTimer data-id="element-79" />
      <FloatingCTA data-id="element-80" />
      <main className="min-h-screen w-full flex flex-col items-center px-4 pt-20 md:pt-24 pb-24 md:pb-20 overflow-x-hidden" data-id="element-81">
        <div className="w-full max-w-3xl space-y-24" data-id="element-82">
          {/* HERO SECTION */}
          <section className="space-y-8 text-center" data-id="element-83">
            <FadeIn data-id="element-84">
              <div className="inline-block border border-brand/30 bg-brand/10 text-brand px-4 py-1.5 rounded-full text-sm font-semibold tracking-widest uppercase mb-6" data-id="element-85">
                Manual Táctico
              </div>
              <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6 text-zinc-50" data-id="element-86">
                PROTOCOLO "HORA EXTRA": <br data-id="element-87" />
                <span className="text-brand" data-id="element-88">
                  El manual táctico para recuperar el mando en la cama en 14
                  días.
                </span>
              </h1>
              <h2 className="text-xl md:text-2xl text-zinc-400 font-sans font-normal leading-relaxed max-w-2xl mx-auto" data-id="element-89">
                Triplicá tu duración real y eliminá la ansiedad de desempeño sin
                pastillas, sprays ni trucos que no funcionan.
              </h2>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-90">
              <div className="bg-[#111] border border-zinc-800 p-6 md:p-8 rounded-lg text-left mt-10 shadow-2xl" data-id="element-91">
                <p className="text-lg leading-relaxed text-zinc-300" data-id="element-92">
                  Conocés perfectamente ese silencio. Terminaste antes de lo que
                  querías, ella se da vuelta, el colchón deja de sonar y vos te
                  quedás mirando el techo en la oscuridad. Es el silencio más
                  largo del mundo.
                </p>
                <p className="text-lg leading-relaxed text-zinc-300 mt-4" data-id="element-93">
                  No es que no seas suficiente hombre. Es que tu cuerpo está
                  operando con un "software" de supervivencia que te traiciona
                  cuando más necesitás que responda. Estás atrapado en un ciclo
                  de ansiedad que acelera tu reloj biológico, y cada vez que
                  fallás, el miedo a la próxima vez se hace más grande.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.3} data-id="element-94">
              <div className="pt-6" data-id="element-95">
                <CTAButton data-id="element-96" />
              </div>
            </FadeIn>
          </section>

          <div className="h-px w-full bg-gradient-to-r from-transparent via-brand/20 to-transparent" data-id="element-97" />

          {/* THE PROBLEM */}
          <section data-id="element-98">
            <FadeIn data-id="element-99">
              <h3 className="text-3xl md:text-4xl font-bold mb-6 text-brand uppercase" data-id="element-100">
                El Problema Real
              </h3>
              <div className="space-y-4 text-lg text-zinc-400 leading-relaxed" data-id="element-101">
                <p data-id="element-102">
                  La mayoría de los hombres intenta solucionar esto con consejos
                  que solo empeoran el problema. "Pensar en el fútbol" solo te
                  distrae y te quita placer. Usar doble preservativo te
                  anestesia. Y las pastillas... bueno, nadie quiere depender de
                  una química que te deja el corazón a mil y te quita la
                  espontaneidad.
                </p>
                <p data-id="element-103">
                  El problema no está en tus genitales.{' '}
                  <strong className="text-zinc-200" data-id="element-104">
                    Está en la comunicación entre tu cerebro y tu sistema
                    nervioso.
                  </strong>{' '}
                  Cuando te ponés ansioso por rendir, tu cuerpo entra en modo
                  "lucha o huida" y acelera el final para "ponerte a salvo". Tu
                  cuerpo cree que estás en peligro, cuando en realidad deberías
                  estar disfrutando.
                </p>
              </div>
            </FadeIn>
          </section>

          {/* WHY IT DIDN'T WORK */}
          <section data-id="element-105">
            <FadeIn data-id="element-106">
              <h3 className="text-3xl md:text-4xl font-bold mb-8 text-zinc-100 uppercase" data-id="element-107">
                Por qué lo que intentaste{' '}
                <span className="text-red-500" data-id="element-108">no funcionó</span>
              </h3>
              <div className="grid gap-4" data-id="element-109">
                {[{
                title: 'Los sprays retardantes',
                desc: 'Solo anestesian la zona, quitándote todo el placer a vos y a ella, convirtiendo el sexo en un trámite mecánico.'
              }, {
                title: "La técnica de 'aguantar'",
                desc: 'Tensar los glúteos y abdominales en realidad le avisa a tu cerebro que debe eyacular lo antes posible.'
              }, {
                title: 'La masturbación previa',
                desc: 'Solo agota tu energía y te deja sin potencia para el encuentro real, bajando tu testosterona.'
              }, {
                title: 'El silencio y la disculpa',
                desc: "Pedir perdón después del hecho solo refuerza el trauma psicológico de que 'algo está roto' en vos."
              }].map((item, i) => <div key={i} className="flex items-start gap-4 bg-[#151515] p-5 rounded border border-zinc-800/50" data-id="element-110">
                    <XCircle className="text-red-500 shrink-0 mt-1" size={24} data-id="element-111" />
                    <div data-id="element-112">
                      <h4 className="font-bold text-zinc-200 text-xl mb-1" data-id="element-113">
                        {item.title}
                      </h4>
                      <p className="text-zinc-400" data-id="element-114">{item.desc}</p>
                    </div>
                  </div>)}
              </div>
            </FadeIn>
          </section>

          {/* THE UNIQUE MECHANISM */}
          <section data-id="element-115">
            <FadeIn data-id="element-116">
              <div className="bg-brand/5 border border-brand/20 p-8 md:p-10 rounded-lg relative overflow-hidden" data-id="element-117">
                <div className="absolute top-0 right-0 w-64 h-64 bg-brand/10 blur-3xl rounded-full -mr-32 -mt-32 pointer-events-none" data-id="element-118" />

                <h3 className="text-3xl md:text-4xl font-bold mb-6 text-brand uppercase relative z-10" data-id="element-119">
                  El Freno de Mano Cognitivo
                </h3>
                <p className="text-lg text-zinc-300 leading-relaxed mb-6 relative z-10" data-id="element-120">
                  Presentamos el Freno de Mano Cognitivo. Este no es un libro de
                  autoayuda. Es un manual de instrucciones para tu sistema
                  nervioso simpático.
                </p>
                <p className="text-lg text-zinc-300 leading-relaxed relative z-10" data-id="element-121">
                  A través de la{' '}
                  <strong className="text-white" data-id="element-122">
                    Relajación Selectiva Aguda
                  </strong>{' '}
                  y el <strong className="text-white" data-id="element-123">Anclaje Sensorial</strong>
                  , aprenderás a "resetear" tu nivel de excitación en 3 segundos
                  sin detener el acto. Es la capacidad de sentir todo el placer,
                  pero sin que el interruptor del final se dispare antes de
                  tiempo.
                </p>
              </div>
            </FadeIn>
          </section>

          <FadeIn data-id="element-124">
            <CTAButton data-id="element-125" />
          </FadeIn>

          <div className="h-px w-full bg-gradient-to-r from-transparent via-brand/20 to-transparent" data-id="element-126" />

          {/* WHAT YOU GET */}
          <section data-id="element-127">
            <FadeIn data-id="element-128">
              <h3 className="text-3xl md:text-4xl font-bold mb-8 text-zinc-100 uppercase text-center" data-id="element-129">
                Qué incluye el manual
              </h3>
              <div className="grid sm:grid-cols-2 gap-4" data-id="element-130">
                {[{
                icon: BookOpen,
                title: 'Ebook Protocolo Hora Extra (PDF)',
                desc: 'El sistema paso a paso de 14 días para reconfigurar tu duración.'
              }, {
                icon: Target,
                title: 'Mapa de Diagnóstico 3x3',
                desc: 'Para saber exactamente qué técnica aplicar según tu nivel de ansiedad.'
              }, {
                icon: Wind,
                title: "Guía de 'Respiración de Combate'",
                desc: "Cómo usar tu oxígeno para bajar las pulsaciones y ganar 'aire' en los momentos críticos."
              }, {
                icon: Shield,
                title: 'Manual de Posiciones de Seguridad',
                desc: 'Las 3 posiciones que te dan control total de la fricción mientras recuperás el ritmo.'
              }, {
                icon: Zap,
                title: "BONUS: Protocolo 'Reset 600'",
                desc: "El plan de 10 minutos para recuperar tu erección y confianza después de una falla (ideal para el 'segundo round').",
                highlight: true
              }].map((item, i) => <div key={i} className={`p-6 rounded-lg border ${item.highlight ? 'bg-brand/10 border-brand/30 sm:col-span-2' : 'bg-[#111] border-zinc-800'}`} data-id="element-131">
                    <item.icon className={`mb-4 ${item.highlight ? 'text-brand' : 'text-zinc-500'}`} size={32} data-id="element-132" />
                    <h4 className={`font-bold text-xl mb-2 ${item.highlight ? 'text-brand' : 'text-zinc-200'}`} data-id="element-133">
                      {item.title}
                    </h4>
                    <p className="text-zinc-400" data-id="element-134">{item.desc}</p>
                  </div>)}
              </div>
            </FadeIn>
          </section>

          {/* WHO THIS IS FOR / NOT FOR */}
          <section className="grid md:grid-cols-2 gap-8" data-id="element-135">
            <FadeIn data-id="element-136">
              <div className="bg-[#111] border border-zinc-800 p-6 rounded-lg h-full" data-id="element-137">
                <h3 className="text-2xl font-bold mb-6 text-zinc-100 uppercase flex items-center gap-2" data-id="element-138">
                  <CheckCircle2 className="text-green-500" data-id="element-139" /> Para quién es
                </h3>
                <ul className="space-y-4" data-id="element-140">
                  {['Hombres de 25 a 45 años que sienten que su cuerpo va más rápido que su mente.', 'Hombres que evitan encuentros sexuales por miedo a terminar demasiado pronto.', 'Parejas que han perdido la química porque el encuentro dura menos de lo que tarda en calentar el agua del mate.', "Hombres que quieren recuperar la seguridad y el orgullo de saber que 'tienen el mapa' de la situación."].map((text, i) => <li key={i} className="flex items-start gap-3 text-zinc-400" data-id="element-141">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2 shrink-0" data-id="element-142" />
                      <span data-id="element-143">{text}</span>
                    </li>)}
                </ul>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-144">
              <div className="bg-[#111] border border-zinc-800 p-6 rounded-lg h-full" data-id="element-145">
                <h3 className="text-2xl font-bold mb-6 text-zinc-100 uppercase flex items-center gap-2" data-id="element-146">
                  <XOctagon className="text-red-500" data-id="element-147" /> Para quién NO es
                </h3>
                <ul className="space-y-4" data-id="element-148">
                  {['Hombres que buscan una pastilla mágica que solucione todo sin practicar ninguna técnica.', 'Personas con patologías orgánicas graves que requieren cirugía o intervención clínica.', 'Hombres que no están dispuestos a dedicar 5 minutos al día a los ejercicios de control.'].map((text, i) => <li key={i} className="flex items-start gap-3 text-zinc-400" data-id="element-149">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 shrink-0" data-id="element-150" />
                      <span data-id="element-151">{text}</span>
                    </li>)}
                </ul>
              </div>
            </FadeIn>
          </section>

          {/* HOW IT WORKS */}
          <section data-id="element-152">
            <FadeIn data-id="element-153">
              <h3 className="text-3xl md:text-4xl font-bold mb-10 text-center text-zinc-100 uppercase" data-id="element-154">
                Cómo funciona
              </h3>
              <div className="space-y-6 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-zinc-800 before:to-transparent" data-id="element-155">
                {[{
                icon: Activity,
                title: 'Identificación',
                desc: "Usás el cuadro de diagnóstico para entender en qué punto del 'reloj biológico' te perdés."
              }, {
                icon: Crosshair,
                title: 'Calibración',
                desc: 'Aplicás el Freno de Mano Cognitivo y la Respiración de Combate durante 14 días.'
              }, {
                icon: Clock,
                title: 'Ejecución',
                desc: 'Aplicás las técnicas en la cama y disfrutás de un control que nunca pensaste que podías tener.'
              }].map((step, i) => <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-156">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-[#0A0A0A] bg-zinc-900 text-brand shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-157">
                      <step.icon size={20} data-id="element-158" />
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-[#151515] p-5 rounded border border-zinc-800" data-id="element-159">
                      <div className="text-brand font-bold text-sm mb-1 uppercase tracking-wider" data-id="element-160">
                        Fase {i + 1}
                      </div>
                      <h4 className="font-bold text-xl text-zinc-200 mb-2" data-id="element-161">
                        {step.title}
                      </h4>
                      <p className="text-zinc-400" data-id="element-162">{step.desc}</p>
                    </div>
                  </div>)}
              </div>
            </FadeIn>
          </section>

          {/* PROOF */}
          <section data-id="element-163">
            <FadeIn data-id="element-164">
              <div className="bg-zinc-900 border-l-4 border-brand p-6 md:p-8" data-id="element-165">
                <h3 className="text-2xl md:text-3xl font-bold mb-4 text-zinc-100 uppercase" data-id="element-166">
                  Qué cambia en 7 días:
                </h3>
                <p className="text-lg text-zinc-300 leading-relaxed mb-4" data-id="element-167">
                  Empezarás a notar que ya no "entrás en pánico" cuando sentís
                  que el placer sube. Vas a identificar el músculo exacto que
                  dispara el final y vas a saber cómo relajarlo mientras seguís
                  en movimiento.
                </p>
                <p className="text-xl font-bold text-brand" data-id="element-168">
                  Por primera vez, vas a ser vos quien decide cuándo termina la
                  noche.
                </p>
              </div>
            </FadeIn>
          </section>

          {/* FAQ */}
          <section data-id="element-169">
            <FadeIn data-id="element-170">
              <h3 className="text-3xl md:text-4xl font-bold mb-8 text-center text-zinc-100 uppercase" data-id="element-171">
                Preguntas Frecuentes
              </h3>
              <div className="bg-[#111] border border-zinc-800 rounded-lg p-2 md:p-6" data-id="element-172">
                <FAQItem question="¿Se nota que estoy aplicando las técnicas?" answer="No, son totalmente internas y discretas. Ella solo notará que estás más presente y que durás mucho más." data-id="element-173" />
                <FAQItem question="¿Es seguro?" answer="100%. No hay químicos ni objetos extraños. Es puro control biológico." data-id="element-174" />
                <FAQItem question="¿Cómo recibo el material?" answer="Al instante. Una vez realizado el pago, te llega el acceso al PDF a tu mail para que lo leas en total privacidad desde tu celular." data-id="element-175" />
              </div>
            </FadeIn>
          </section>

          {/* OFFER & FINAL CTA */}
          <section id="pricing" className="text-center pb-12" data-id="element-176">
            <FadeIn data-id="element-177">
              <div className="bg-gradient-to-b from-[#151515] to-[#0A0A0A] border border-brand/30 p-8 md:p-12 rounded-2xl shadow-[0_0_40px_rgba(212,160,23,0.05)]" data-id="element-178">
                <h3 className="text-2xl md:text-3xl font-bold mb-4 text-zinc-100" data-id="element-179">
                  Recuperá tu seguridad y transformá tu vida sexual hoy mismo
                  por menos de lo que cuesta una cena afuera.
                </h3>

                {/* Price Anchoring */}
                <div className="my-8" data-id="element-180">
                  <div className="text-zinc-500 uppercase tracking-widest text-sm font-bold mb-3" data-id="element-181">
                    Precio Especial de Lanzamiento (Arg)
                  </div>
                  <div className="md:text-xl line-through mb-1 text-[20px] text-[#8F8F9C]" data-id="element-182">
                    Precio Regular: $24.700 ARS
                  </div>
                  <div className="text-6xl md:text-8xl font-display font-bold text-brand leading-none" data-id="element-183">
                    $8.990
                  </div>
                  <div className="text-xl text-zinc-500 font-bold mt-1" data-id="element-184">
                    ARS
                  </div>
                </div>

                <CTAButton className="max-w-md mx-auto mb-8" data-id="element-185" />

                {/* Guarantee Block */}
                <div className="bg-[#0A0A0A]/60 border border-zinc-700 rounded-lg p-5 md:p-6 max-w-lg mx-auto text-left" data-id="element-186">
                  <div className="flex items-start gap-3" data-id="element-187">
                    <span className="text-2xl shrink-0 mt-0.5" data-id="element-188">🛡️</span>
                    <div data-id="element-189">
                      <h4 className="text-zinc-200 font-bold text-sm uppercase tracking-wider mb-2" data-id="element-190">
                        Garantía de Satisfacción
                      </h4>
                      <p className="text-zinc-400 text-sm leading-relaxed" data-id="element-191">
                        Si el protocolo no cumple con tus expectativas en los
                        primeros 7 días, te devolvemos el 100% de tu dinero. Sin
                        preguntas. Tu inversión está protegida.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Payment Logo Placeholders */}
                <div className="mt-8 flex flex-col items-center gap-4" data-id="element-192">
                  <div className="flex items-center justify-center gap-6" data-id="element-193">
                    <div className="w-20 h-14 bg-white rounded-lg flex items-center justify-center p-2" data-id="element-194">
                      <img src="https://cdn.magicpatterns.com/uploads/mDLK65gWivEhiAkSdqoZ5r/Captura_de_pantalla_8-4-2026_212350_www.google.com.jpg" alt="Visa" className="max-h-full max-w-full object-contain" data-id="element-195" />
                    </div>
                    <div className="w-20 h-14 bg-white rounded-lg flex items-center justify-center p-1 overflow-hidden" data-id="element-196">
                      <img src="https://cdn.magicpatterns.com/uploads/9NEFqC8cbdHd6ntfAeb6LA/Captura_de_pantalla_8-4-2026_211944_www.mercadopago.com.ar.jpg" alt="Mercado Pago" className="max-h-full max-w-full object-contain scale-125" data-id="element-197" />
                    </div>
                    <div className="w-20 h-14 bg-white rounded-lg flex items-center justify-center p-2" data-id="element-198">
                      <img src="https://cdn.magicpatterns.com/uploads/kvFvaYnaXP6mtg1t5QX67T/Captura_de_pantalla_2026-04-15_203807.png" alt="Mastercard" className="max-h-full max-w-full object-contain" data-id="element-199" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-500 text-sm" data-id="element-200">
                    <span data-id="element-201">🔒</span>
                    <span data-id="element-202">Pago procesado de forma segura y 100% discreta.</span>
                  </div>
                </div>
              </div>
            </FadeIn>
          </section>

          {/* PAYMENT METHODS & LEGAL */}
          <section data-id="element-203">
            <FadeIn data-id="element-204">
              <PaymentMethods data-id="element-205" />
            </FadeIn>
          </section>
        </div>
      </main>
      <Footer data-id="element-206" />
    </>;
}